/**
 * Constants for Purchase Flow
 */

import type { Screen, FlowMode } from '../types';

// ============================================================================
// Screen Constants
// ============================================================================

export const SCREENS = {
  PURCHASE_DETAILS: 'purchase_details',
  PAYMENT_METHODS_LIST: 'payment_methods_list',
  ADD_CREDIT_CARD: 'add_credit_card',
  PAYMENT_PROCESSING: 'payment_processing'
} as const satisfies Record<string, Screen>;

export const INITIAL_SCREEN: Screen = SCREENS.PURCHASE_DETAILS;

/**
 * Payment source error text constants
 * These match error messages from the API
 */
export const PAYMENT_SOURCE_ERRORS = {
  CANNOT_BE_USED_ON_THIS_BROWSER: 'Cannot be used on this browser',
  PAYMENT_METHOD_CANNOT_BE_USED: 'Payment method cannot be used',
  PLEASE_RE_ENTER_CARD_DETAILS: 'Please re-enter the card details for security reasons',
  CANNOT_BE_USED_AT_THIS_TIME: 'Cannot be used at this time'
} as const;

/**
 * Payment processing error types
 * These are returned in the errorType field of API responses
 */
export const PAYMENT_ERROR_TYPES = {
  SPENDING_LIMITS: 'spending_limits',
  POPUP_BLOCKED: 'popup_blocked',
  /** Exchange rate changed since the purchase was initiated */
  RATE_CHANGED: 'rate_changed'
} as const;

// ============================================================================
// Icon Keys (for renderIcon)
// ============================================================================

/**
 * All icon identifiers used by the purchase flow.
 * Passed to `renderIcon(key)` — the consumer must handle each key.
 *
 * Navigation icons are fixed strings.
 * Payment method icons use the card label (e.g., 'visa', 'mastercard')
 * via getCardType(), which is dynamic, so those are not listed here.
 */
export const PURCHASE_FLOW_ICONS = {
  BACK: 'back',
  CLOSE: 'close',
  ARROW_RIGHT: 'arrow_right',
  ERROR: 'error',
  /** Success checkmark icon (matches old purchases_success) */
  SUCCESS: 'success',
  /** Large error icon shown at the top of the processing error modal (matches old purchases_flow_error) */
  PROCESSING_ERROR: 'processing_error',
  /** Error icon rendered inside content for mobile/inline mode (matches old mobile_processing_status_error_icon) */
  PROCESSING_ERROR_MOBILE: 'processing_error_mobile',
  // Known payment method icon identifiers (used by getCardType)
  PAYPAL: 'paypal',
  APPLE_PAY: 'apple_pay',
  GOOGLE_PAY: 'google_pay',
  UNKNOWN_CARD: 'UNKNOWN',
  // Add payment method icons (used by PaymentOptionsList / PaymentMethodsListScreen)
  ADD_CREDIT_CARD: 'add_cc',
  ADD_PAYPAL: 'add_pp',
  ADD_APPLE_PAY: 'add_ap',
  ADD_GOOGLE_PAY: 'add_gp',
  // In-App Purchase: iap = option/card display (iap_icon); ADD_IAP = add button (add_iap with version logic)
  IAP: 'iap',
  ADD_IAP: 'add_iap',
  // Unsupported/invalid payment method icon
  PAYMENT_METHOD_INVALID: 'payment_method_invalid',
  // Credit card form icons
  LOCK: 'lock',
  GUARANTEED_SAFE: 'guaranteed_safe',
  CC_INFO: 'cc_info',
  // PAYG inline validation error icon (matches old CreditCardValidateProcessing error_icon)
  VALIDATION_ERROR: 'validation_error',
  // Payment source error indicator (red circle icon shown next to expired/error cards)
  PAYMENT_SOURCE_ERROR: 'payment_source_error',
  // Payment method details — delete/remove icon (top right of card)
  DELETE: 'delete_icon',
  // Empty wallet / no payment methods placeholder
  PLACEHOLDER: 'placeholder',
  /** App logo image — shown in the Popover overlay for affiliate promo success */
  APP_LOGO: 'app_logo'
} as const;

// ============================================================================
// Text Keys (for renderText)
// ============================================================================

/**
 * All translation keys used by the purchase flow.
 * Passed to `renderText(key, params?)` — the consumer must handle each key.
 *
 * Params (when applicable) are documented alongside each key.
 */
export const PURCHASE_FLOW_TEXT_KEYS = {
  // Screen titles (no params)
  /** "Payment information" — PAYG mode purchase details. */
  TITLE_PAYMENT_INFORMATION: 'purchaseFlow.title.paymentInformation',
  /** "Additional funds required" — additionalFunds mode. */
  TITLE_ADDITIONAL_FUNDS: 'purchaseFlow.title.additionalFunds',
  /** "Claim your gift now!" — affiliatePromo mode. */
  TITLE_CLAIM_GIFT: 'purchaseFlow.title.claimGift',
  /** "Purchase credit" — default purchase mode. */
  TITLE_PURCHASE_CREDIT: 'purchaseFlow.title.purchaseCredit',
  /** "Payment Methods" — payment methods list screen. */
  TITLE_PAYMENT_METHODS: 'purchaseFlow.title.paymentMethods',
  /** "Add Payment Method" — add credit card screen. */
  TITLE_ADD_PAYMENT_METHOD: 'purchaseFlow.title.addPaymentMethod',
  /** "Processing" — payment processing screen (credit card). */
  TITLE_PROCESSING: 'purchaseFlow.title.processing',
  /** "Your payment is processing in another window…" — PayPal processing. No params. */
  PROCESSING_EXTERNAL_WINDOW: 'purchaseFlow.processing.externalWindow',
  /** "here" — redirect link text appended after the external window message. No params. */
  PROCESSING_REDIRECT_LINK: 'purchaseFlow.processing.redirectLink',
  // Buttons
  /** Pay button. Params: { amount: string } — amount already includes the currency sign (e.g. "$1.00", "MXN 1.00") */
  BUTTON_PAY: 'purchaseFlow.button.pay',
  /** Start session button (PAYG mode). No params. */
  BUTTON_START_SESSION: 'purchaseFlow.button.startSession',
  /** Affiliate promo button title. Params: { amount: string } — amount already includes the currency sign */
  BUTTON_ADD_GET_BONUS: 'purchaseFlow.button.addGetBonus',
  /** Affiliate promo button bonus suffix. Params: { amount: string } — amount already includes the currency sign */
  BUTTON_ADD_GET_BONUS_SUFFIX: 'purchaseFlow.button.addGetBonusSuffix',
  /** "Maybe later" — affiliate promo dismiss link. No params. */
  PROMO_MAYBE_LATER: 'purchaseFlow.promo.maybeLater',
  /** "Offer expires {dateString} (at midnight EST)" — rich text with bold/br components. Params: { dateString: string } */
  PROMO_OFFER_EXPIRES: 'purchaseFlow.promo.offerExpires',

  // Details toggle
  /** Show details link. No params. */
  DETAILS_SHOW: 'purchaseFlow.details.show',
  /** Hide details link. No params. */
  DETAILS_HIDE: 'purchaseFlow.details.hide',

  // Payment options
  /** "Please select a payment method:" — shown when multiple options and no default. No params. */
  SELECT_OPTION: 'purchaseFlow.selectOption',
  /** "Continue with Apple Pay". No params. */
  OPTION_CONTINUE_APPLE_PAY: 'purchaseFlow.option.continueApplePay',
  /** "Continue with PayPal". No params. */
  OPTION_CONTINUE_PAYPAL: 'purchaseFlow.option.continuePayPal',
  /** "Continue with Google Pay". No params. */
  OPTION_CONTINUE_GOOGLE_PAY: 'purchaseFlow.option.continueGooglePay',
  /** "Set up Apple Pay". No params. */
  OPTION_SETUP_APPLE_PAY: 'purchaseFlow.option.setupApplePay',
  /** "Add a new debit or credit card". No params. */
  OPTION_ADD_CARD: 'purchaseFlow.option.addCard',
  /** "Add payment method". No params. */
  OPTION_ADD_PAYMENT_METHOD: 'purchaseFlow.option.addPaymentMethod',
  /** "In-app purchase". No params. */
  OPTION_IN_APP_PURCHASE: 'purchaseFlow.option.inAppPurchase',

  // Payment methods list
  /** "No saved payment methods". No params. */
  NO_SAVED_METHODS: 'purchaseFlow.noSavedMethods',
  /** "This is your default payment method". No params. */
  DEFAULT_PAYMENT_METHOD: 'purchaseFlow.defaultPaymentMethod',
  /** "Your previous default payment option is not supported on this device or browser." No params. */
  PAYMENT_NOT_SUPPORTED_MESSAGE: 'purchaseFlow.paymentNotSupportedMessage',
  /** "Please select a different payment method" — unsupported payment card prompt. No params. */
  SELECT_DIFFERENT_PAYMENT_METHOD: 'purchaseFlow.selectDifferentPaymentMethod',
  // PAYG ineligibility — per-kind messages (maps to existing legacy translations)
  /** "This card cannot be used for Pay as you go mode". No params. */
  PAYG_INELIGIBLE_CARD: 'purchaseFlow.paygIneligible.card',
  /** "PayPal is not available for this service type…". No params. */
  PAYG_INELIGIBLE_PAYPAL: 'purchaseFlow.paygIneligible.paypal',
  /** "Google Pay is not available for this service type…". No params. */
  PAYG_INELIGIBLE_GOOGLE_PAY: 'purchaseFlow.paygIneligible.googlePay',
  /** "Apple Pay is not available for this service type…". No params. */
  PAYG_INELIGIBLE_APPLE_PAY: 'purchaseFlow.paygIneligible.applePay',

  // Screen titles (conditional)
  /** "Minimum balance required:" — additionalFunds mode when amount is below minimum. No params. */
  TITLE_MINIMUM_BALANCE: 'purchaseFlow.title.minimumBalance',

  // Details slot content
  /** "Required funds:" — details section label. No params. */
  DETAILS_REQUIRED_FUNDS: 'purchaseFlow.details.requiredFunds',
  /** "Required funds (X min.):" — details section label with duration. Params: { mins: number } */
  DETAILS_REQUIRED_FUNDS_WITH_MIN: 'purchaseFlow.details.requiredFundsWithMin',
  /** "Current balance:" — details section label. No params. */
  DETAILS_CURRENT_BALANCE: 'purchaseFlow.details.currentBalance',
  /** "Additional funds required:" — details section label. No params. */
  DETAILS_ADDITIONAL_FUNDS: 'purchaseFlow.details.additionalFunds',

  // Minimum purchase note
  /** "* You must purchase at least $X.XX. Unused funds will remain…". Params: { price: string } */
  MIN_PURCHASE_NOTE: 'purchaseFlow.minPurchaseNote',

  // Credit card form — Labels
  /** "Cardholder name" — input label. No params. */
  CC_LABEL_NAME: 'purchaseFlow.cc.label.name',
  /** "Card Number" — input label. No params. */
  CC_LABEL_CARD_NUMBER: 'purchaseFlow.cc.label.cardNumber',
  /** "Expiration" — input label. No params. */
  CC_LABEL_EXPIRY: 'purchaseFlow.cc.label.expiry',
  /** "Security Code" — input label. No params. */
  CC_LABEL_CVV: 'purchaseFlow.cc.label.cvv',
  /** "Zip / Postal Code" — input label. No params. */
  CC_LABEL_ZIP: 'purchaseFlow.cc.label.zip',

  // Credit card form — Validation errors
  /** "Please enter cardholder name" — name validation error. No params. */
  CC_ERROR_NAME: 'purchaseFlow.cc.error.name',
  /** "Please enter a valid zip code" — zip validation error. No params. */
  CC_ERROR_ZIP: 'purchaseFlow.cc.error.zip',
  /** "Incorrect card number" — gateway validation error. No params. */
  CC_ERROR_CARD_NUMBER: 'purchaseFlow.cc.error.cardNumber',
  /** "Incorrect CVV number" — gateway validation error. No params. */
  CC_ERROR_CVV: 'purchaseFlow.cc.error.cvv',
  /** "Incorrect expiration date" — gateway validation error. No params. */
  CC_ERROR_EXPIRY: 'purchaseFlow.cc.error.expiry',

  // Credit card form — Buttons & labels
  /** "Save Card" — save button text. No params. */
  CC_SAVE_CARD: 'purchaseFlow.cc.saveCard',
  /** "Guaranteed Safe" — guaranteed safe section text. No params. */
  CC_GUARANTEED_SAFE: 'purchaseFlow.cc.guaranteedSafe',
  /** PAYG disclaimer on add credit card (supports credit and non-prepaid cards). May contain <br/>. */
  CC_PAYG_DISCLAIMER: 'purchaseFlow.cc.paygDisclaimer',

  // Credit card form — Error popup
  /** Error popup title (e.g. "Credit Card issue"). No params. */
  CC_ERROR_POPUP_TITLE: 'purchaseFlow.cc.errorPopup.title',
  /** Error popup default message. No params. */
  CC_ERROR_POPUP_MESSAGE: 'purchaseFlow.cc.errorPopup.message',
  /** Error popup OK button text. No params. */
  CC_ERROR_POPUP_OK: 'purchaseFlow.cc.errorPopup.ok',

  // Credit card form — Tooltips
  /** Expiry tooltip text. No params. */
  CC_TOOLTIP_EXPIRY: 'purchaseFlow.cc.tooltip.expiry',
  /** CVV tooltip text. No params. */
  CC_TOOLTIP_CVV: 'purchaseFlow.cc.tooltip.cvv',

  // Processing screen — Error state
  /** Default error title (e.g. "Purchase error"). No params. */
  ERROR_TITLE: 'purchaseFlow.processing.errorTitle',
  /** "Increase spending limit" button for spending limits error. No params. */
  BUTTON_INCREASE_SPENDING_LIMIT: 'purchaseFlow.button.increaseSpendingLimit',
  /** Generic OK button. No params. */
  BUTTON_OK: 'purchaseFlow.button.ok',
  /** Success button when continuing a session (additionalFunds + isContinue). No params. */
  BUTTON_CONTINUE: 'purchaseFlow.button.continue',
  /** Success button before starting chat (additionalFunds, !isContinue). No params. */
  BUTTON_START_CHAT: 'purchaseFlow.button.startChat',
  /** Success button before starting video call. No params. */
  BUTTON_START_VIDEO_CALL: 'purchaseFlow.button.startVideoCall',
  /** Success button before starting voice call. No params. */
  BUTTON_START_VOICE_CALL: 'purchaseFlow.button.startVoiceCall',
  /** Retry button. No params. */
  BUTTON_RETRY: 'purchaseFlow.button.retry',

  // Processing screen — Popup blocked error (Google Pay on Safari)
  /** "Enable Google Pay" — popup blocked error title. No params. */
  ERROR_POPUP_BLOCKED_TITLE: 'purchaseFlow.processing.popupBlockedTitle',
  /** Popup blocked instructions message (consumer should pick mobile vs desktop variant). No params. */
  ERROR_POPUP_BLOCKED_MESSAGE: 'purchaseFlow.processing.popupBlockedMessage',

  // Processing screen — Success state (cashback)
  /** "This purchase has earned you" — cashback prefix. No params. */
  CASHBACK_EARNED: 'purchaseFlow.processing.cashbackEarned',
  /** "Cashback" — cashback label. No params. */
  CASHBACK_LABEL: 'purchaseFlow.processing.cashbackLabel',

  // PAYG inline validation — Error state
  /** "Card validation failed" — inline validation error title. No params. */
  VALIDATION_ERROR_TITLE: 'purchaseFlow.validation.errorTitle',
  /** "There was a failure validating your card..." — inline validation error message. No params. */
  VALIDATION_ERROR_MESSAGE: 'purchaseFlow.validation.errorMessage',

  // Payment sources management page
  /** "Payment methods" — main payment sources page title. No params. */
  PAYMENT_SOURCES_TITLE: 'purchaseFlow.paymentSources.title',
  /** "Payment method info" — details screen title. No params. */
  PAYMENT_SOURCES_DETAILS_TITLE: 'purchaseFlow.paymentSources.detailsTitle',
  /** "Add Payment Method" — add card screen title. No params. */
  PAYMENT_SOURCES_ADD_CARD_TITLE: 'purchaseFlow.paymentSources.addCardTitle',
  /** "You currently have no saved payment methods." — empty state. No params. */
  PAYMENT_SOURCES_EMPTY: 'purchaseFlow.paymentSources.empty',
  /** Wallet add-card button when user has no saved methods — maps to purchase.add_a_new_payment_method. */
  PAYMENT_SOURCES_ADD_NEW_PAYMENT_METHOD: 'purchaseFlow.paymentSources.addNewPaymentMethod',
  /** Wallet add-card button when user already has methods — maps to purchase.add_another_payment_method. */
  PAYMENT_SOURCES_ADD_ANOTHER_PAYMENT_METHOD: 'purchaseFlow.paymentSources.addAnotherPaymentMethod',
  /** "Set as default payment method" — set default button. No params. */
  PAYMENT_SOURCES_SET_DEFAULT: 'purchaseFlow.paymentSources.setDefault',
  /** "Delete payment method" — remove button. No params. */
  PAYMENT_SOURCES_REMOVE: 'purchaseFlow.paymentSources.remove',
  /** "Cancel" — cancel button. No params. */
  PAYMENT_SOURCES_CANCEL: 'purchaseFlow.paymentSources.cancel',
  /** "Delete payment method" — confirmation dialog title. No params. */
  PAYMENT_SOURCES_REMOVE_CONFIRM_TITLE: 'purchaseFlow.paymentSources.removeConfirmTitle',
  /** "Are you sure you want to delete this payment method?" — confirmation dialog message. No params. */
  PAYMENT_SOURCES_REMOVE_CONFIRM_MESSAGE: 'purchaseFlow.paymentSources.removeConfirmMessage',
  /** "You must select a new default..." — cannot remove default error. No params. */
  PAYMENT_SOURCES_CANNOT_REMOVE_DEFAULT: 'purchaseFlow.paymentSources.cannotRemoveDefault',
  /** "Failed to set default payment method" — set default error. No params. */
  PAYMENT_SOURCES_SET_DEFAULT_ERROR: 'purchaseFlow.paymentSources.setDefaultError',
  /** "Failed to remove payment method" — remove error. No params. */
  PAYMENT_SOURCES_REMOVE_ERROR: 'purchaseFlow.paymentSources.removeError',
  /** "Default payment updated" — toast after setting default. No params. */
  PAYMENT_SOURCES_DEFAULT_UPDATED: 'purchaseFlow.paymentSources.defaultUpdated',
  /** "Yes, delete it" — confirmation dialog confirm button. No params. */
  PAYMENT_SOURCES_CONFIRM_DELETE: 'purchaseFlow.paymentSources.confirmDelete',

  // Affiliate promo success screen
  /** "Balance: ${{amount}}" — total balance headline. Params: { amount: string } */
  AFFILIATE_PROMO_SUCCESS_BALANCE: 'purchaseFlow.affiliatePromoSuccess.balance',
  /** Congratulations message — used via resolveTextKey() with Trans component. Params: { amount: number } */
  AFFILIATE_PROMO_SUCCESS_CONGRATS: 'purchaseFlow.affiliatePromoSuccess.congrats',
  /** "If you need anything from us, " — contact us section prefix. No params. */
  AFFILIATE_PROMO_SUCCESS_CONTACT_PREFIX: 'purchaseFlow.affiliatePromoSuccess.contactPrefix',
  /** "contact us " — contact us link label. No params. */
  AFFILIATE_PROMO_SUCCESS_CONTACT_LINK_TEXT: 'purchaseFlow.affiliatePromoSuccess.contactLinkText',
  /** "any time!" — contact us section suffix. No params. */
  AFFILIATE_PROMO_SUCCESS_CONTACT_SUFFIX: 'purchaseFlow.affiliatePromoSuccess.contactSuffix'
} as const;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Returns the PURCHASE_FLOW_TEXT_KEYS key for the screen title.
 * The consumer's `renderText` handles the actual translation.
 *
 * @param screen - Current screen
 * @param isPayg - Whether in Pay-As-You-Go mode
 * @param mode - Flow mode that affects titles
 * @param isMinPurchase - Whether in minimum purchase mode (additionalFunds)
 */
export const getScreenTitleKey = (
  screen: Screen,
  isPayg = false,
  mode: FlowMode = 'purchase',
  isMinPurchase = false
): string => {
  switch (screen) {
    case SCREENS.PURCHASE_DETAILS:
      if (isPayg) return PURCHASE_FLOW_TEXT_KEYS.TITLE_PAYMENT_INFORMATION;
      if (mode === 'additionalFunds') {
        return isMinPurchase
          ? PURCHASE_FLOW_TEXT_KEYS.TITLE_MINIMUM_BALANCE
          : PURCHASE_FLOW_TEXT_KEYS.TITLE_ADDITIONAL_FUNDS;
      }
      if (mode === 'affiliatePromo') return PURCHASE_FLOW_TEXT_KEYS.TITLE_CLAIM_GIFT;
      return PURCHASE_FLOW_TEXT_KEYS.TITLE_PURCHASE_CREDIT;
    case SCREENS.PAYMENT_METHODS_LIST:
      return PURCHASE_FLOW_TEXT_KEYS.TITLE_PAYMENT_METHODS;
    case SCREENS.ADD_CREDIT_CARD:
      return PURCHASE_FLOW_TEXT_KEYS.TITLE_ADD_PAYMENT_METHOD;
    case SCREENS.PAYMENT_PROCESSING:
      return PURCHASE_FLOW_TEXT_KEYS.TITLE_PROCESSING;
    default:
      return '';
  }
};
